/**
 * Schema Definitions
 * 
 * SINGLE SOURCE OF TRUTH for database schema.
 * When you add a new field to the TypeScript types, add it here too.
 * The Data Diagnostics page will tell you what SQL to run.
 */

export interface ColumnDefinition {
  name: string;           // Column name in database (snake_case)
  tsName: string;         // Property name in TypeScript (camelCase)
  type: string;           // PostgreSQL type
  nullable: boolean;
  description: string;
}

export interface TableSchema {
  tableName: string;
  columns: ColumnDefinition[];
}

// ============================================================================
// ITEMS TABLE (v2.2)
// ============================================================================

export const ITEMS_SCHEMA: TableSchema = {
  tableName: 'items',
  columns: [
    // Core fields (required)
    { name: 'id', tsName: 'id', type: 'UUID', nullable: false, description: 'Primary key' },
    { name: 'title', tsName: 'title', type: 'TEXT', nullable: false, description: 'Item title' },
    { name: 'description', tsName: 'description', type: 'TEXT', nullable: true, description: 'Item description' },
    { name: 'created_at', tsName: 'createdAt', type: 'TIMESTAMPTZ', nullable: false, description: 'Creation timestamp' },
    { name: 'updated_at', tsName: 'updatedAt', type: 'TIMESTAMPTZ', nullable: false, description: 'Last update timestamp' },
    { name: 'created_by', tsName: 'createdBy', type: 'TEXT', nullable: true, description: 'User who created' },
    { name: 'workspace_id', tsName: 'workspace_id', type: 'TEXT', nullable: true, description: 'Multi-tenant workspace ID' },
    
    // Lifecycle fields
    { name: 'lifecycle_state', tsName: 'lifecycle_state', type: 'TEXT', nullable: false, description: 'item (priority is contextual)' },
    { name: 'exit_status', tsName: 'exit_status', type: 'TEXT', nullable: false, description: 'active | deferred | archived — canonical item exit status' },
    { name: 'lifecycle_stage', tsName: 'lifecycle_stage', type: 'TEXT', nullable: true, description: 'Deprecated. Use exit_status.' },
    { name: 'detailed_state', tsName: 'detailedState', type: 'TEXT', nullable: true, description: 'Deprecated. Use exit_status.' },
    
    // Classification
    { name: 'source', tsName: 'source', type: 'TEXT', nullable: true, description: 'Where the idea came from' },
    { name: 'tags', tsName: 'tags', type: 'TEXT[]', nullable: true, description: 'Array of tag strings' },
    { name: 'item_category', tsName: 'itemCategory', type: 'TEXT', nullable: true, description: 'problem | opportunity | solution | etc' },
    { name: 'altitude', tsName: 'altitude', type: 'TEXT', nullable: true, description: 'outcome | initiative | epic | feature | story | task' },
    
    // Organization
    { name: 'org_unit_id', tsName: 'orgUnitId', type: 'TEXT', nullable: true, description: 'Owning org unit' },
    { name: 'queue_id', tsName: 'queueId', type: 'TEXT', nullable: true, description: 'Queue this item was submitted to' },
    
    // Hierarchy
    { name: 'parent_item_id', tsName: 'parentItemId', type: 'TEXT', nullable: true, description: 'Parent item for hierarchy' },
    
    // Deduplication
    { name: 'dedupe_status', tsName: 'dedupeStatus', type: 'TEXT', nullable: true, description: 'unique | merged | duplicate_of | pending' },
    { name: 'duplicate_of_id', tsName: 'duplicateOfId', type: 'TEXT', nullable: true, description: 'If duplicate, points to canonical' },
    
    // Shaping workflow
    { name: 'shaping_owner_id', tsName: 'shapingOwnerId', type: 'TEXT', nullable: true, description: 'User doing item work on this item' },
    { name: 'shaping_started_at', tsName: 'shapingStartedAt', type: 'TIMESTAMPTZ', nullable: true, description: 'When item work began' },
    { name: 'shaped_at', tsName: 'shapedAt', type: 'TIMESTAMPTZ', nullable: true, description: 'When item work completed' },
    
    // Readiness
    { name: 'ready_for_ranking', tsName: 'readyForRanking', type: 'BOOLEAN', nullable: true, description: 'Whether item can be ranked' },
    { name: 'shaping_completeness', tsName: 'shapingCompleteness', type: 'INTEGER', nullable: true, description: '0-100 completeness score' },
  ],
};

// ============================================================================
// IDEA_SUBMISSIONS TABLE
// ============================================================================

export const IDEA_SUBMISSIONS_SCHEMA: TableSchema = {
  tableName: 'idea_submissions',
  columns: [
    // Core fields
    { name: 'id', tsName: 'id', type: 'UUID', nullable: false, description: 'Primary key' },
    { name: 'title', tsName: 'title', type: 'TEXT', nullable: false, description: 'Submission title' },
    { name: 'description', tsName: 'description', type: 'TEXT', nullable: true, description: 'Submission description' },
    { name: 'submitted_at', tsName: 'submittedAt', type: 'TIMESTAMPTZ', nullable: false, description: 'When submitted' },
    { name: 'submitted_by', tsName: 'submittedBy', type: 'TEXT', nullable: true, description: 'Username who submitted' },
    { name: 'submitter_id', tsName: 'submitterId', type: 'TEXT', nullable: true, description: 'User ID who submitted' },
    { name: 'submitter_name', tsName: 'submitterName', type: 'TEXT', nullable: true, description: 'Display name' },
    { name: 'workspace_id', tsName: 'workspace_id', type: 'TEXT', nullable: true, description: 'Multi-tenant workspace ID' },
    
    // Status
    { name: 'status', tsName: 'status', type: 'TEXT', nullable: false, description: 'INTAKE_PENDING | SHAPING | READY | etc' },
    { name: 'category', tsName: 'category', type: 'TEXT', nullable: true, description: 'Category/queue label' },
    { name: 'priority', tsName: 'priority', type: 'TEXT', nullable: true, description: 'Priority level' },
    
    // Queue routing
    { name: 'queue_id', tsName: 'queueId', type: 'TEXT', nullable: true, description: 'Queue (org unit) routed to' },
    
    // Linking to items
    { name: 'promoted_to_item_id', tsName: 'promotedToItemId', type: 'TEXT', nullable: true, description: 'Linked item ID once promoted' },
  ],
};

// ============================================================================
// ORG_UNITS TABLE
// ============================================================================

export const ORG_UNITS_SCHEMA: TableSchema = {
  tableName: 'org_units',
  columns: [
    { name: 'id', tsName: 'id', type: 'UUID', nullable: false, description: 'Primary key' },
    { name: 'name', tsName: 'name', type: 'TEXT', nullable: false, description: 'Internal name' },
    { name: 'display_name', tsName: 'displayName', type: 'TEXT', nullable: false, description: 'Display name' },
    { name: 'type', tsName: 'type', type: 'TEXT', nullable: false, description: 'organization | division | department | team | etc' },
    { name: 'parent_id', tsName: 'parentId', type: 'TEXT', nullable: true, description: 'Parent org unit' },
    { name: 'path', tsName: 'path', type: 'TEXT[]', nullable: true, description: 'Array of ancestor IDs' },
    { name: 'depth', tsName: 'depth', type: 'INTEGER', nullable: false, description: 'Depth in hierarchy' },
    { name: 'description', tsName: 'description', type: 'TEXT', nullable: true, description: 'Description' },
    { name: 'active', tsName: 'active', type: 'BOOLEAN', nullable: false, description: 'Is active' },
    { name: 'created_at', tsName: 'createdAt', type: 'TIMESTAMPTZ', nullable: false, description: 'Creation timestamp' },
    { name: 'updated_at', tsName: 'updatedAt', type: 'TIMESTAMPTZ', nullable: false, description: 'Last update timestamp' },
    { name: 'workspace_id', tsName: 'workspace_id', type: 'TEXT', nullable: true, description: 'Multi-tenant workspace ID' },
    
    // Queue settings (stored as JSONB)
    { name: 'queue_settings', tsName: 'queueSettings', type: 'JSONB', nullable: true, description: 'Queue configuration JSON' },
  ],
};

// ============================================================================
// ALL SCHEMAS
// ============================================================================

export const ALL_SCHEMAS: TableSchema[] = [
  ITEMS_SCHEMA,
  IDEA_SUBMISSIONS_SCHEMA,
  ORG_UNITS_SCHEMA,
];

// ============================================================================
// SQL GENERATION HELPERS
// ============================================================================

/**
 * Generate SQL to add missing columns to a table
 */
export function generateAddColumnSQL(tableName: string, columns: ColumnDefinition[]): string {
  if (columns.length === 0) return '-- No columns to add';
  
  const statements = columns.map(col => {
    const nullConstraint = col.nullable ? '' : ' NOT NULL';
    return `ALTER TABLE ${tableName} ADD COLUMN IF NOT EXISTS ${col.name} ${col.type}${nullConstraint};`;
  });
  
  return `-- Add missing columns to ${tableName}\n${statements.join('\n')}`;
}

/**
 * Generate full CREATE TABLE SQL for reference
 */
export function generateCreateTableSQL(schema: TableSchema): string {
  const columnDefs = schema.columns.map(col => {
    const nullConstraint = col.nullable ? '' : ' NOT NULL';
    const pkConstraint = col.name === 'id' ? ' PRIMARY KEY' : '';
    return `  ${col.name} ${col.type}${nullConstraint}${pkConstraint}`;
  });
  
  return `CREATE TABLE IF NOT EXISTS ${schema.tableName} (\n${columnDefs.join(',\n')}\n);`;
}
